import { useABAC } from '@adornis/abac/client/useABAC.js';
import { getMyPermissionAccess } from '@adornis/abac/policies/SimpleRolePolicy.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { isClientFeatureEnabled } from '@nsc/clients/client-feature-flags.js';
import { WorkflowType } from '@nsc/clients/entities/Client.js';
import {
  type ClientFeatureKey,
  FEATURE_LABELS,
  getFeaturesForWorkflow,
} from '@nsc/clients/entities/client-features.js';
import { NexusPermission } from '@nsc/nexus-permissions/platform-rbac/nexus-permissions.js';
import { subscribeDellTicketsWorkedCount } from '@nsc/workflows-dell/ticketing/api/subscribe-dell-tickets-worked-count.js';
import { subscribeFlashGlobalSchedulingsInitiatedCount } from '@nsc/workflows-partner-scheduling/api/subscribe-flash-global-schedulings-initiated-count.js';
import { subscribeFlashGlobalTicketsWorkedCount } from '@nsc/workflows-partner-scheduling/api/subscribe-flash-global-tickets-worked-count.js';
import { subscribeVerizonSchedulingsInitiatedCount } from '@nsc/workflows-verizon/ticketing/api/subscribe-verizon-schedulings-initiated-count.js';
import { subscribeVerizonTicketsWorkedCount } from '@nsc/workflows-verizon/ticketing/api/subscribe-verizon-tickets-worked-count.js';
import { html, nothing } from 'lit';
import { subscribeAccessibleClients } from '../../api/nexus-scoped-clients.js';

const CLIENT_ACTIONS_BY_WORKFLOW: Record<
  WorkflowType,
  {
    title: string;
    description: string;
  }
> = {
  [WorkflowType.DELLFRANCE]: {
    title: 'Dell',
    description: 'Dell scheduling and route planning tools',
  },
  [WorkflowType.OSS]: {
    title: 'OSS',
    description: 'Workflow: OSS',
  },
  [WorkflowType.VERIZON]: {
    title: 'Verizon',
    description: 'Workflow: Verizon',
  },
  [WorkflowType.PARKLAND]: {
    title: 'Parkland',
    description: 'Workflow: Parkland',
  },
  [WorkflowType.UNKNOWN]: {
    title: 'Unknown',
    description: 'Unknown workflow',
  },
};

export const ClientWorkspaceActions = stateful(
  (props: { client: NarrowedEntity<Client, { _id: 1; name: 1; enabled: 1; workflowType: 1; enabledFeatures: 1 }> }) => {
    const { client } = props;
    const wf = client.workflowType ?? WorkflowType.UNKNOWN;
    const clientId = String(client._id);

    const { value: viewAccess, loading: viewLoading } = useQuery(
      () => getMyPermissionAccess(NexusPermission.ClientView, clientId)({ hasAccess: 1 }),
      [clientId],
    );
    const { value: killAccess } = useQuery(
      () => getMyPermissionAccess(NexusPermission.ClientKillSwitch, clientId)({ hasAccess: 1 }),
      [clientId],
    );

    const canView = viewAccess?.hasAccess === true;
    const canKill = killAccess?.hasAccess === true;

    const renderClientActions = () => {
      if (/flash global/i.test(client.name ?? '')) return FlashGlobalTools({ clientId });

      switch (wf) {
        case WorkflowType.DELLFRANCE:
          return DellTools({ clientId });
        case WorkflowType.VERIZON:
          return VerizonTools({ clientId });
      }
    };

    const killSwitch = () =>
      !canKill
        ? nothing
        : html` <input
            type="checkbox"
            class="toggle toggle-primary shrink-0"
            .checked=${!!client.enabled}
            @change=${() => {
              client.enabled = !client.enabled;
              void client.save?.();
            }}
          />`;

    const toggleFeature = (feature: ClientFeatureKey) => {
      const current = client.enabledFeatures ?? [];
      client.enabledFeatures = current.includes(feature) ? current.filter(f => f !== feature) : [...current, feature];
      void client.save?.();
    };

    // Per-feature switches for this client's workflow type (Dell exposes more than Verizon, etc.).
    // Reuses the ClientKillSwitch permission (`canKill`) since toggling a feature is a Client edit.
    // Greyed out while the master killswitch is off, because nothing runs then anyway.
    const featureToggles = () => {
      const features = getFeaturesForWorkflow(wf);
      if (!canKill || features.length === 0) return nothing;
      return html`
        <div class="flex flex-col gap-1.5 mt-2 border-t border-neutral/10 pt-3">
          <p class="text-sm font-semibold text-neutral/70">Workflow features</p>
          ${features.map(
            feature => html`
              <label class="flex items-center justify-between gap-3 cursor-pointer">
                <span class="text-sm text-neutral/80">${FEATURE_LABELS[feature]}</span>
                <input
                  type="checkbox"
                  class="toggle toggle-sm toggle-primary shrink-0"
                  ?disabled=${client.enabled === false}
                  .checked=${isClientFeatureEnabled(client, feature)}
                  @change=${() => toggleFeature(feature)}
                />
              </label>
            `,
          )}
        </div>
      `;
    };

    if (viewLoading) {
      return html`<span class="loading loading-spinner loading-md"></span>`;
    }
    if (!canView) return nothing;

    const { title, description } = CLIENT_ACTIONS_BY_WORKFLOW[wf];

    return html`
      <section class="card bg-base-100 shadow-xl rounded-xl border border-neutral/10 min-w-0">
        <div class="card-body flex flex-col gap-2">
          <header class="flex flex-col gap-0.5 min-w-0">
            <p class="text-sm text-neutral/50 font-medium">${client.name ?? clientId}</p>
          </header>
          <div class="flex items-center justify-between gap-2 flex-row">
            <div class="flex flex-col gap-1">
              <h2 class="card-title text-xl font-bold text-neutral">${title}</h2>
              <p class="text-base text-neutral/60">${description}</p>
            </div>
            <div class="flex items-center gap-3">${killSwitch()}</div>
          </div>
          ${featureToggles()} ${renderClientActions()}
        </div>
      </section>
    `;
  },
);

const DellTools = stateful((props: { clientId: string }) => {
  const clients = useObservable(() => subscribeAccessibleClients()({ _id: 1, workflowType: 1 }), []);
  const list = (Array.isArray(clients) ? clients : []) as Client[];
  const hasDellFrance = list.some(c => c.workflowType === WorkflowType.DELLFRANCE);

  const { value: statsAccess } = useQuery(
    () => getMyPermissionAccess(NexusPermission.ClientStats, props.clientId)({ hasAccess: 1 }),
    [props.clientId],
  );
  const { value: planAccess } = useQuery(
    () => getMyPermissionAccess(NexusPermission.RoutePlanning, props.clientId)({ hasAccess: 1 }),
    [props.clientId],
  );
  const { value: schedAccess } = useQuery(
    () => getMyPermissionAccess(NexusPermission.Scheduling, props.clientId)({ hasAccess: 1 }),
    [props.clientId],
  );

  const canStats = statsAccess?.hasAccess === true;
  const canPlan = planAccess?.hasAccess === true;
  const canSched = schedAccess?.hasAccess === true;

  const ticketsWorkedCount = useQuery(
    () => (canStats ? subscribeDellTicketsWorkedCount()() : Promise.resolve([0, 0])),
    [canStats],
  );

  if (!hasDellFrance) return nothing;
  if (!canStats && !canPlan && !canSched) return nothing;

  return html`
    <div class="flex flex-col gap-2">
      ${canStats
        ? html`<div class="stats shadow bg-base-100 border border-neutral/10">
            <div class="stat">
              <div class="stat-title">Tickets worked on (this month)</div>
              <div class="stat-value text-primary">${ticketsWorkedCount.value?.[0] ?? 0}</div>
            </div>
            <div class="stat">
              <div class="stat-title">Tickets worked on (previous month)</div>
              <div class="stat-value text-secondary">${ticketsWorkedCount.value?.[1] ?? 0}</div>
            </div>
          </div>`
        : nothing}
      <div class="flex gap-2 flex-wrap mt-2">
        ${canPlan
          ? html`<a class="btn btn-outline btn-primary w-fit" href="/route-planning">Route Planning</a>`
          : nothing}
        ${canPlan
          ? html`<a class="btn btn-outline btn-primary w-fit" href="/route-planning/plannings">Route Plannings</a>`
          : nothing}
        ${canStats
          ? html`<a class="btn btn-outline btn-accent w-fit" href="/route-planning/ticket-stats">Ticket Stats</a>`
          : nothing}
        ${canSched
          ? html`<a class="btn btn-outline btn-secondary w-fit" href="/scheduling-debug">Manual Scheduling</a>`
          : nothing}
      </div>
    </div>
  `;
});

const VerizonTools = stateful((props: { clientId: string }) => {
  const clients = useObservable(() => subscribeAccessibleClients()({ _id: 1, workflowType: 1 }), []);
  const list = (Array.isArray(clients) ? clients : []) as Client[];
  const hasVerizon = list.some(c => c.workflowType === WorkflowType.VERIZON);

  const { value: statsAccess } = useQuery(
    () => getMyPermissionAccess(NexusPermission.ClientStats, props.clientId)({ hasAccess: 1 }),
    [props.clientId],
  );
  const canStats = statsAccess?.hasAccess === true;

  const ticketsWorkedCount = useQuery(
    () => (canStats ? subscribeVerizonTicketsWorkedCount()() : Promise.resolve([0, 0])),
    [canStats],
  );
  const schedulingsInitiatedCount = useQuery(
    () => (canStats ? subscribeVerizonSchedulingsInitiatedCount()() : Promise.resolve([0, 0])),
    [canStats],
  );

  if (!hasVerizon) return nothing;
  if (!canStats) return nothing;

  return html`
    <div class="flex flex-col gap-2">
      <div class="stats shadow bg-base-100 border border-neutral/10">
        <div class="stat">
          <div class="stat-title">Tickets worked on (this month)</div>
          <div class="stat-value text-primary">${ticketsWorkedCount.value?.[0] ?? 0}</div>
        </div>
        <div class="stat">
          <div class="stat-title">Tickets worked on (previous month)</div>
          <div class="stat-value text-secondary">${ticketsWorkedCount.value?.[1] ?? 0}</div>
        </div>
        <div class="stat">
          <div class="stat-title">Partner schedulings initiated (this month)</div>
          <div class="stat-value text-primary">${schedulingsInitiatedCount.value?.[0] ?? 0}</div>
        </div>
        <div class="stat">
          <div class="stat-title">Partner schedulings initiated (previous month)</div>
          <div class="stat-value text-secondary">${schedulingsInitiatedCount.value?.[1] ?? 0}</div>
        </div>
      </div>
    </div>
  `;
});

const FlashGlobalTools = stateful((props: { clientId: string }) => {
  const { value: statsAccess } = useQuery(
    () => getMyPermissionAccess(NexusPermission.ClientStats, props.clientId)({ hasAccess: 1 }),
    [props.clientId],
  );
  const canStats = statsAccess?.hasAccess === true;

  const ticketsWorkedCount = useQuery(
    () => (canStats ? subscribeFlashGlobalTicketsWorkedCount()() : Promise.resolve([0, 0])),
    [canStats],
  );
  const schedulingsInitiatedCount = useQuery(
    () => (canStats ? subscribeFlashGlobalSchedulingsInitiatedCount()() : Promise.resolve([0, 0])),
    [canStats],
  );

  if (!canStats) return nothing;

  return html`
    <div class="flex flex-col gap-2">
      <div class="stats shadow bg-base-100 border border-neutral/10">
        <div class="stat">
          <div class="stat-title">Tickets worked on (this month)</div>
          <div class="stat-value text-primary">${ticketsWorkedCount.value?.[0] ?? 0}</div>
        </div>
        <div class="stat">
          <div class="stat-title">Tickets worked on (previous month)</div>
          <div class="stat-value text-secondary">${ticketsWorkedCount.value?.[1] ?? 0}</div>
        </div>
        <div class="stat">
          <div class="stat-title">Partner schedulings initiated (this month)</div>
          <div class="stat-value text-primary">${schedulingsInitiatedCount.value?.[0] ?? 0}</div>
        </div>
        <div class="stat">
          <div class="stat-title">Partner schedulings initiated (previous month)</div>
          <div class="stat-value text-secondary">${schedulingsInitiatedCount.value?.[1] ?? 0}</div>
        </div>
      </div>
    </div>
  `;
});
